import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError } from "@core/http";
import { Icon } from "@core/icons";
import { recoverPassword } from "./api";
import { Shell, BackButton } from "./AccessLayout";
import { BigButton, Field, Notice } from "./primitives";
import "./access.css";

// SPEC-301 §5 · `validador.jsx:157-190` (`Recuperar`).
//
// Decisión humana ya tomada (ver el encargo de esta corrida): "Ya tengo el
// enlace" (`validador.jsx:176`) se retira — ningún RF-VAL lo pide y no puede
// funcionar, porque `PUT /v1/validator/access/password` exige `token` en el
// cuerpo y ese token solo llega por el correo. El contrato visual ya lo
// quitó (`context/agent-os/visual-validation/screens.json`,
// `validator.access-recover`), así que este componente no lo reintroduce.
export function RecoverPasswordScreen() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState<{ validityHours: number } | null>(null);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!email.includes("@")) {
      setError("Ingresa un correo válido.");
      return;
    }
    setError(null);
    setSubmitting(true);
    // La respuesta es la misma exista o no la cuenta (RF-VAL-06) — pero esa
    // no-revelación la resuelve el 200 del backend, no acá. Un error real
    // (400 de formato, 5xx, red) sí debe distinguirse: tratarlo como éxito
    // silencioso deja al usuario esperando un correo que nunca se envió.
    await recoverPassword(email)
      .then((result) => {
        setSent({ validityHours: result.validityHours });
      })
      .catch((err: unknown) => {
        const isBadFormat = err instanceof ApiError && err.status === 400;
        setError(
          isBadFormat
            ? "Ingresa un correo válido."
            : "No se pudo procesar la solicitud. Intenta de nuevo.",
        );
      })
      .finally(() => {
        setSubmitting(false);
      });
  }

  return (
    <Shell>
      <div className="sc-screen sc-screen--compact">
        <BackButton
          onClick={() => {
            navigate("/validator/access-login");
          }}
        />

        {sent ? (
          <div>
            <div className="sc-sent-icon">
              <Icon name="check" size={31} color="var(--gold-2)" stroke={2.2} />
            </div>
            <h1 className="sc-title">Revisa tu correo</h1>
            <p className="sc-copy sc-copy--tight">
              Enviamos un enlace a{" "}
              <b style={{ color: "var(--text)" }}>{email}</b>.
              {/* ADR-026: la vigencia es un parámetro (`security.
                  recovery_ttl_hours`), no un número fijo del prototipo
                  (`validador.jsx:171` decía "30 minutos"). Se muestra lo que
                  devuelve `data.validityHours`
                  (`api/contracts/parts/50-validator.yaml:38-40`). */}
              {sent.validityHours > 0
                ? ` Es válido por ${String(sent.validityHours)} horas y sirve una sola vez.`
                : " Sirve una sola vez."}
            </p>
            <p className="sc-copy-muted">
              Si no lo encuentras, revisa la carpeta de correo no deseado.
            </p>
            <BigButton
              tone="ghost"
              onClick={() => {
                setSent(null);
              }}
            >
              Reenviar el enlace
            </BigButton>
          </div>
        ) : (
          <form onSubmit={(event) => void handleSubmit(event)}>
            <h1 className="sc-title">Recuperar contraseña</h1>
            <p className="sc-copy">
              Escribe el correo con el que fue registrado tu usuario del
              comercio. Te enviaremos un enlace para crear una nueva contraseña.
            </p>
            {error ? <Notice>{error}</Notice> : null}
            <Field
              label="Correo"
              kind="email"
              value={email}
              onChange={(value) => {
                setEmail(value);
                setError(null);
              }}
              placeholder="nombre@comercio.com"
              error={Boolean(error)}
            />
            <BigButton type="submit" disabled={submitting}>
              {submitting ? "Enviando…" : "Enviar enlace"}
            </BigButton>
          </form>
        )}
        <div className="sc-flex-1" />
      </div>
    </Shell>
  );
}
